import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FacilityDialog } from "@/components/common/FacilityDialog";
import { Button } from "@/components/ui/button";
import { Pencil, Hospital, Building2 } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value }) {
  return <div className="flex justify-between py-2.5 border-b border-slate-100 last:border-0"><span className="text-sm text-slate-500">{label}</span><span className="text-sm font-medium text-slate-800 text-right">{value || "—"}</span></div>;
}

export default function FacilityInfo() {
  const { activeFacility, has, refresh } = useFacility();
  const { auth, loadMe } = useAuth();
  const [open, setOpen] = useState(false);
  const f = activeFacility;

  const save = async (form) => {
    try { await api.put(`/facilities/${f.id}`, form); toast.success("Facility updated"); setOpen(false); await loadMe(); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!f) return <div className="text-slate-400 py-20 text-center">No facility selected.</div>;

  return (
    <div data-testid="facility-info-page">
      <PageHeader title="Facility" subtitle="Identity, address and operational details for the selected facility."
        actions={has("facility:edit") && <Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="edit-facility-button"><Pencil className="h-4 w-4 mr-1.5" /> Edit Facility</Button>} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <span className="h-12 w-12 rounded-md bg-blue-50 text-blue-700 grid place-items-center"><Hospital className="h-6 w-6" /></span>
          <div className="mt-4 font-display text-lg font-semibold text-slate-900">{f.name}</div>
          <div className="text-xs text-slate-400 font-mono mt-1">{f.code}</div>
          <div className="mt-3"><StatusBadge status={f.status} /></div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-sm text-slate-600"><Building2 className="h-4 w-4 text-slate-400" />{auth?.organisation?.name}</div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-display font-semibold text-slate-800 mb-2">Details</h3>
          <div className="grid sm:grid-cols-2 gap-x-8">
            <div><Row label="Facility type" value={f.facility_type} /><Row label="Beds" value={f.beds} /><Row label="City" value={f.city} /><Row label="State" value={f.state} /><Row label="Country" value={f.country} /><Row label="PIN code" value={f.pincode} /></div>
            <div><Row label="Contact number" value={f.contact_number} /><Row label="Email" value={f.email} /><Row label="Registration no." value={f.registration_number} /><Row label="GST number" value={f.gst_number} /><Row label="PAN" value={f.pan} /><Row label="Address" value={f.address} /></div>
          </div>
        </div>
      </div>

      <FacilityDialog open={open} onOpenChange={setOpen} initial={f} onSave={save} title="Edit Facility" />
    </div>
  );
}
